import { useConfig } from '@borealis/huck/client';
import type { Brand } from '@finn-no/mobility-item-insertion-shared';
import pulse from '@finn-no/pulse-sdk';
import type React from 'react';
import { useState } from 'react';
import { DbaBox } from '@/components/dba/DbaBox.js';
import { SolgtComBox } from '@/components/dba/SolgtComBox.js';
import { FinnBox } from '@/components/finn/FinnBox.js';
import { NettbilBox } from '@/components/finn/NettbilBox.js';
import { PageNavigator, PageNumber } from '@/components/PageNavigator.js';
import type { Config } from '../../types.js';

const EmptyComponent: React.FC = () => null;

export const LandingPage: React.FC = () => {
  const [activeBox, setActiveBox] = useState(PageNumber.ONE);

  interface ScrollEvent extends React.UIEvent<HTMLDivElement> {
    target: HTMLDivElement & EventTarget;
  }

  const handleScroll = (e: ScrollEvent): void => {
    const scrollLeft = e.target.scrollLeft;
    const totalWidth = e.target.scrollWidth - e.target.clientWidth;

    if (scrollLeft < totalWidth / 2) {
      setActiveBox(PageNumber.ONE);
    } else {
      setActiveBox(PageNumber.TWO);
    }
  };

  const snapXStyle: React.CSSProperties = {
    scrollSnapType: 'x mandatory',
    overflowX: 'auto',
  };

  const snapCenterStyle = {
    width: '80%',
    scrollSnapAlign: 'center',
  };

  const config: Config = useConfig<Config>();

  const trackCurrentPage = () => {
    pulse.trackPageView({
      name: 'Ad insertion - mobility',
      object: {
        type: 'Page',
        pageType: 'product_description',
        name: document.title,
        stepName: 'Landing Page',
        action: 'Create ClassifiedAd',
      },
      vertical: {
        name: 'mobility',
      },
    });
  };

  if (typeof window !== 'undefined') trackCurrentPage();

  const brandToMarketplaceBoxMap: Record<Brand, React.FC> = {
    finn: FinnBox,
    dba: DbaBox,
    bilbasen: DbaBox, // TODO: Consider creating an "EmptyBox"
    blocket: EmptyComponent, // Empty component for Blocket as it is not used in this page
  };

  const otherBoxMap: Record<Brand, React.FC> = {
    finn: NettbilBox,
    dba: SolgtComBox,
    bilbasen: SolgtComBox, // TODO: Consider creating an "EmptyBox"
    blocket: EmptyComponent, // Empty component for Blocket as it is not used in this page
  };

  const MarketplaceBox = brandToMarketplaceBoxMap[config.brand];
  const OtherBox = otherBoxMap[config.brand];

  const pageTitleByBrand: Record<Brand, string> = {
    finn: 'Hvordan vil du selge bilen?',
    dba: 'Hvordan vil du sælge din bil?',
    bilbasen: 'Hvordan vil du sælge din bil?',
    blocket: '',
  };

  return (
    <main className="mb-64">
      <h1 className="my-16 md:my-32">{pageTitleByBrand[config.brand]}</h1>
      <div className="flex flex-row md:space-y-0 space-x-10 md:space-x-16" onScroll={handleScroll} style={snapXStyle}>
        <div className="shrink-0 md:shrink md:min-w-0 md:max-w-1/2" style={snapCenterStyle}>
          <MarketplaceBox />
        </div>
        <div className="shrink-0 md:shrink md:min-w-0 md:max-w-1/2" style={snapCenterStyle}>
          <OtherBox />
        </div>
      </div>
      <div id="page-navigator" className="md:hidden mt-24 flex justify-center">
        <PageNavigator activeBox={activeBox} />
      </div>
    </main>
  );
};
